import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { peopleKeys } from '../people/keys.ts'
import { householdKeys } from './keys.ts'

/**
 * Household hooks — profile "Household" tabs and the household editor share
 * these on both platforms.
 */

export function useHouseholds(personId?: string) {
  const api = useApi()
  const query = useInfiniteQuery({
    queryKey: householdKeys.list(personId),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.households.get({
        query: {
          ...(pageParam ? { cursor: pageParam } : {}),
          ...(personId ? { personId } : {}),
        },
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
  })

  return {
    households: query.data?.pages.flatMap((p) => p.items) ?? [],
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isLoading: query.isPending,
    isError: query.isError,
  }
}

export function useHousehold(householdId: string) {
  const api = useApi()
  return useQuery({
    queryKey: householdKeys.detail(householdId),
    queryFn: async () => {
      const { data, error } = await api.api.v1.households({ id: householdId }).get()
      if (error) throw error
      return data
    },
  })
}

export type HouseholdDetail = NonNullable<ReturnType<typeof useHousehold>['data']>

function useHouseholdInvalidation() {
  const queryClient = useQueryClient()
  return (data?: { id: string }) => {
    if (data) queryClient.setQueryData(householdKeys.detail(data.id), data)
    void queryClient.invalidateQueries({ queryKey: householdKeys.lists() })
  }
}

export function useCreateHousehold() {
  const api = useApi()
  const invalidate = useHouseholdInvalidation()
  return useMutation({
    mutationFn: async (body: Parameters<typeof api.api.v1.households.post>[0]) => {
      const { data, error } = await api.api.v1.households.post(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => invalidate(data),
  })
}

export function useUpdateHousehold(householdId: string) {
  const api = useApi()
  const invalidate = useHouseholdInvalidation()
  return useMutation({
    mutationFn: async (body: { name?: string | null }) => {
      const { data, error } = await api.api.v1.households({ id: householdId }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => invalidate(data),
  })
}

export function useAddHouseholdMember(householdId: string) {
  const api = useApi()
  const invalidate = useHouseholdInvalidation()
  return useMutation({
    mutationFn: async (body: { personId: string; role?: 'adult' | 'child' }) => {
      const { data, error } = await api.api.v1.households({ id: householdId }).members.post(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => invalidate(data),
  })
}

export function useRemoveHouseholdMember(householdId: string) {
  const api = useApi()
  const invalidate = useHouseholdInvalidation()
  return useMutation({
    mutationFn: async (personId: string) => {
      const { data, error } = await api.api.v1
        .households({ id: householdId })
        .members({ personId })
        .delete()
      if (error) throw error
      return data
    },
    onSuccess: (data) => invalidate(data),
  })
}

export function useSetPrimaryContact(householdId: string) {
  const api = useApi()
  const invalidate = useHouseholdInvalidation()
  return useMutation({
    mutationFn: async (personId: string) => {
      const { data, error } = await api.api.v1
        .households({ id: householdId })
        ['primary-contact'].put({ personId })
      if (error) throw error
      return data
    },
    onSuccess: (data) => invalidate(data),
  })
}

export function useApplyHouseholdAddress(householdId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const invalidate = useHouseholdInvalidation()
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1
        .households({ id: householdId })
        ['apply-address'].post()
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      invalidate(data)
      // Members' primary addresses changed.
      void queryClient.invalidateQueries({ queryKey: peopleKeys.all() })
    },
  })
}
